import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';

import { ButtonTabs, Tab } from '@edu-frontend/modules/src/shared/ui/ButtonTabs';
import { IconBook, IconCode, IconCheckCircle } from '@edu-frontend/modules/src/components/ui/Icon';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { useIntl } from 'react-intl';
import { AsyncFnReturn } from 'react-use/lib/useAsyncFn';
import { LESSON_HEADER_HEIGHT } from '@edu-frontend/modules/src/features/lessonHeaderMobile';
import { PAGINATOR_HEIGHT } from '@edu-frontend/modules/src/shared/ui/PaginatorWithDescription';
import { Helmet } from 'react-helmet';
import cn from 'classnames';
import { CodeHistory } from '@yandex-int/k-common';
import { CodingEditor } from '../__editor/coding-problem__editor';
import { InputSwitch } from '../__input-switch/coding-problem__input-switch';
import { CodingInfo } from '../__info/coding-problem__info';
import { CodingState, useCodingStore } from '../state';
import { CodingVerdictMobile } from '../__verdict/coding-problem__verdict-mobile';
import { getMe } from '../../user/selectors';
import { retrieveFromSessionStorage, saveToSessionStorage } from '../../../utils/web-storage';
import { CodingTests } from '../__tests/coding-problem__tests';
import { useCodingPropsSelector } from '../context';
import { MOBILE_CODE_EDITOR_TABS_HEIGHT } from './consts';

import './coding-problem__mobile-code-editor.scss';

const TABS = [
    {
        id: 'task',
        title: 'codingProblem.mobileEditor.tabs.task',
        Icon: IconBook,
    },
    {
        id: 'code',
        title: 'codingProblem.mobileEditor.tabs.code',
        Icon: IconCode,
    },
];

type MobileCodeEditorProps = {
    runCodeState: AsyncFnReturn[0];
    initialValue: () => string | CodeHistory;
    sessionKey: string;
};

export const MobileCodeEditor = (props: MobileCodeEditorProps) => {
    const { runCodeState, initialValue, sessionKey } = props;

    const intl = useIntl();
    const me = useSelector(getMe);
    const activeCodingTab = useCodingStore((store) => store.activeTab);
    const setActiveCodingTab = useCodingStore((store) => store.setActiveTab);
    const isFocusOnInput = useCodingStore((store) => store.isFocusOnInput);

    const verdictSessionKey = `verdictHintShown-${me?.id}-${JSON.stringify(useParams())}`;
    const initialVerdictHintShown = Boolean(retrieveFromSessionStorage(verdictSessionKey));

    useEffect(() => {
        setActiveCodingTab('task');
    }, [setActiveCodingTab]);

    const [isVerdictHintShown, setIsVerdictHintShown] = useState(initialVerdictHintShown);

    const closeVerdictHintHandler = useCallback(() => {
        saveToSessionStorage(verdictSessionKey, 'true');
        setIsVerdictHintShown(true);
    }, [verdictSessionKey]);

    const markup = useCodingPropsSelector((p) => p.problem.markup);
    const hasTests = useMemo(
        () => markup.inputs.some((input) => typeof input.correct_answer === 'string'),
        [markup.inputs]
    );
    const shouldHideTestsPaginator = activeCodingTab !== 'tests' || isFocusOnInput || markup.inputs.length <= 1;

    const tabs = [
        ...TABS,
        {
            id: 'tests',
            title: hasTests ? 'codingProblem.mobileEditor.tabs.tests' : 'codingProblem.mobileEditor.tabs.output',
            Icon: IconCheckCircle,
        },
    ];

    const renderTabContent = useCallback(
        (tabId: Tab['id']) => {
            switch (tabId) {
                case 'task':
                    return <CodingInfo />;
                case 'code':
                    return (
                        <>
                            <CodingEditor initialValue={initialValue} sessionKey={sessionKey} view="mobile" />
                            {!isVerdictHintShown && !isFocusOnInput && (
                                <CodingVerdictMobile onClose={closeVerdictHintHandler} />
                            )}
                        </>
                    );
                case 'tests':
                    return (
                        <>
                            <CodingTests
                                isMobile
                                runCodeState={runCodeState}
                                style={{
                                    ['--tests-paginator-height']: shouldHideTestsPaginator ? '0px' : PAGINATOR_HEIGHT,
                                }}
                            />
                            {!isVerdictHintShown && <CodingVerdictMobile onClose={closeVerdictHintHandler} />}
                        </>
                    );
                default:
                    return null;
            }
        },
        [
            initialValue,
            sessionKey,
            isVerdictHintShown,
            isFocusOnInput,
            closeVerdictHintHandler,
            shouldHideTestsPaginator,
            runCodeState,
        ]
    );

    return (
        <ThemeProvider isRoot={false} style={{ height: '100%' }} theme={hrLight}>
            {/* Меняем цвет браузера на цвет редактора кода из-за "бага" в мобильном сафари - при открытии клавиатуры между html и клавиатурой образуется дыра */}
            {isFocusOnInput && (
                <Helmet>
                    <meta content="#24292e" name="theme-color" />
                </Helmet>
            )}
            {!isFocusOnInput && (
                <ButtonTabs
                    className="mobile-code-editor__tabs"
                    style={{ ['--mobile-code-editor_header-height']: LESSON_HEADER_HEIGHT } as CSSProperties}
                    tabListLabel={intl.formatMessage({ id: 'a11y.codingProblem.mobileEditor.tabs' })}
                    tabMaxWidth
                    tabs={tabs.map(({ id, title, Icon }) => ({
                        id,
                        title: intl.formatMessage({ id: title }),
                        icon: <Icon size="m" />,
                        isActive: activeCodingTab === id,
                        onClick: (tabId: CodingState['activeTab']) => {
                            setActiveCodingTab(tabId);
                            if (window) {
                                window.scrollTo(0, 0);
                            }
                        },
                    }))}
                />
            )}
            <div
                className={cn(
                    'mobile-code-editor__tabs-content-wrapper',
                    isFocusOnInput && 'mobile-code-editor__tabs-content-wrapper_with-focus'
                )}
                style={
                    {
                        ['--mobile-code-editor_header-height']: LESSON_HEADER_HEIGHT,
                        ['--mobile-code-editor_tabs-height']: isFocusOnInput ? '0px' : MOBILE_CODE_EDITOR_TABS_HEIGHT,
                    } as CSSProperties
                }
            >
                <InputSwitch
                    aria-label={
                        hasTests
                            ? 'a11y.problemCoding.testsPaginatorButton'
                            : 'a11y.problemCoding.outputPaginatorButton'
                    }
                    hidden={shouldHideTestsPaginator}
                    label={hasTests ? 'problemCoding.tests' : 'problemCoding.output'}
                    view="mobile"
                />
                {renderTabContent(activeCodingTab)}
            </div>
        </ThemeProvider>
    );
};
